import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { DAY_TYPES, MYZMANIM_FIELDS, PRAYER_TYPES } from '../../config/constants';
import { DayType, PrayerType, ScheduleEntry, TimeRule } from '../../types';
import ScheduleEntryForm, { ScheduleEntryFormData } from './ScheduleEntryForm';

type ViewMode = 'prayer' | 'day';

interface ScheduleListProps {
  schedules: ScheduleEntry[];
  onAdd: (data: ScheduleEntryFormData) => void;
  onUpdate: (entryId: string, data: ScheduleEntryFormData) => void;
  onDelete: (entryId: string) => void;
  onReorder: (newSchedules: ScheduleEntry[]) => void;
}

function formatTimeRule(rule: TimeRule, t: (key: string) => string): string {
  if (rule.type === 'fixed') {
    return `${t('admin.fixedTime')} ${rule.time}`;
  }

  const zmanLabel = rule.baseZman === 'candleLighting' ? t('zmanim.candleLighting') : MYZMANIM_FIELDS[rule.baseZman] || rule.baseZman;
  const sign = rule.offsetMinutes >= 0 ? '+' : '';
  let summary = `${zmanLabel} ${sign}${rule.offsetMinutes} ${t('admin.minutesShort')}`;

  if (rule.rounding) {
    const directionKey = `admin.round${rule.rounding.direction.charAt(0).toUpperCase()}${rule.rounding.direction.slice(1)}`;
    summary += `, ${t('admin.roundDirection')} ${t(directionKey)} ${t('admin.roundTo')} ${rule.rounding.to}`;
  }

  return summary;
}

function orderedDayTypes(entry: ScheduleEntry): DayType[] {
  if (!Array.isArray(entry.dayTypes) || entry.dayTypes.length === 0) return [];
  const known = DAY_TYPES.filter((d) => entry.dayTypes.includes(d));
  const extras = entry.dayTypes.filter((d) => !DAY_TYPES.includes(d));
  return [...known, ...extras];
}

function groupByPrayer(schedules: ScheduleEntry[]): { prayerType: PrayerType; entries: ScheduleEntry[] }[] {
  return PRAYER_TYPES.map((prayerType) => ({
    prayerType,
    entries: schedules.filter((s) => s.prayerType === prayerType),
  })).filter((g) => g.entries.length > 0);
}

function groupByDay(schedules: ScheduleEntry[]): { dayType: DayType; entries: ScheduleEntry[] }[] {
  return DAY_TYPES.map((dayType) => ({
    dayType,
    entries: schedules.filter(
      (s) => Array.isArray(s.dayTypes) && s.dayTypes.includes(dayType)
    ),
  })).filter((g) => g.entries.length > 0);
}

interface EntryBodyProps {
  entry: ScheduleEntry;
  isHebrew: boolean;
  t: (key: string) => string;
  onEdit: () => void;
  onDelete: () => void;
}

function EntryBody({ entry, isHebrew, t, onEdit, onDelete }: EntryBodyProps) {
  return (
    <>
      <div className="min-w-0 space-y-1.5">
        <p className="text-sm font-medium text-ink">
          {t(`prayers.${entry.prayerType}`)}
          {!entry.active && (
            <span className="ms-2 text-xs font-normal text-ash">
              ({t('admin.inactive')})
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {orderedDayTypes(entry).map((d) => (
            <span
              key={d}
              className="text-xs px-2 py-0.5 rounded-[10px] font-medium bg-fog text-ink"
            >
              {t(`dayTypes.${d}`)}
            </span>
          ))}
          {entry.prayerType === 'custom' && entry.specificDate && (
            <span className="text-xs px-2 py-0.5 rounded-[10px] font-medium bg-fog text-ink">
              {entry.specificDate}
            </span>
          )}
        </div>
        <p className="text-sm text-ink">{formatTimeRule(entry.timeRule, t)}</p>
        {(entry.note || entry.noteHe) && (
          <p className="text-sm text-ash">
            {isHebrew ? entry.noteHe || entry.note : entry.note || entry.noteHe}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-ink hover:underline"
        >
          {t('common.edit')}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-sm text-danger-600 hover:underline"
        >
          {t('common.delete')}
        </button>
      </div>
    </>
  );
}

interface SortableEntryRowProps {
  entry: ScheduleEntry;
  isHebrew: boolean;
  t: (key: string) => string;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableEntryRow({ entry, isHebrew, t, onEdit, onDelete }: SortableEntryRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : undefined,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded-[10px] border border-ink/10 px-3 py-2 bg-paper"
    >
      <button
        type="button"
        className="mt-0.5 shrink-0 touch-none p-1 -ms-1 text-ash hover:text-ink cursor-grab active:cursor-grabbing"
        aria-label={t('admin.dragToReorder')}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
        <EntryBody
          entry={entry}
          isHebrew={isHebrew}
          t={t}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </li>
  );
}

interface PrayerGroupProps {
  prayerType: PrayerType;
  entries: ScheduleEntry[];
  schedules: ScheduleEntry[];
  editingId: string | null;
  isHebrew: boolean;
  t: (key: string) => string;
  onEdit: (id: string) => void;
  onCancelEdit: () => void;
  onUpdate: (entryId: string, data: ScheduleEntryFormData) => void;
  onDelete: (entryId: string) => void;
  onReorder: (newSchedules: ScheduleEntry[]) => void;
}

function PrayerGroup({
  prayerType,
  entries,
  schedules,
  editingId,
  isHebrew,
  t,
  onEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
  onReorder,
}: PrayerGroupProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = entries.findIndex((e) => e.id === active.id);
    const newIndex = entries.findIndex((e) => e.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedGroup = arrayMove(entries, oldIndex, newIndex);
    const newSchedules = PRAYER_TYPES.flatMap((pt) =>
      pt === prayerType
        ? reorderedGroup
        : schedules.filter((s) => s.prayerType === pt)
    );
    onReorder(newSchedules);
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-ash uppercase tracking-wide">
        {t(`prayers.${prayerType}`)}
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={entries.map((e) => e.id)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="space-y-2">
            {entries.map((entry) =>
              editingId === entry.id ? (
                <li key={entry.id}>
                  <ScheduleEntryForm
                    entry={entry}
                    allSchedules={schedules}
                    onSave={(data) => {
                      onUpdate(entry.id, data);
                      onCancelEdit();
                    }}
                    onCancel={onCancelEdit}
                  />
                </li>
              ) : (
                <SortableEntryRow
                  key={entry.id}
                  entry={entry}
                  isHebrew={isHebrew}
                  t={t}
                  onEdit={() => onEdit(entry.id)}
                  onDelete={() => onDelete(entry.id)}
                />
              )
            )}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default function ScheduleList({
  schedules,
  onAdd,
  onUpdate,
  onDelete,
  onReorder,
}: ScheduleListProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('prayer');

  const handleDelete = (entryId: string) => {
    if (window.confirm(t('admin.confirmDeleteSchedule'))) {
      onDelete(entryId);
    }
  };

  const viewModes: { id: ViewMode; label: string }[] = [
    { id: 'prayer', label: t('admin.groupByPrayer') },
    { id: 'day', label: t('admin.groupByDay') },
  ];

  const prayerGroups = groupByPrayer(schedules);
  const dayGroups = groupByDay(schedules);

  return (
    <div className="bg-paper rounded-[16px] border border-ink/10 p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-ink">{t('admin.schedules')}</h3>
        {!adding && (
          <button
            type="button"
            onClick={() => {
              setAdding(true);
              setEditingId(null);
            }}
            className="text-sm text-ink hover:underline"
          >
            {t('admin.addSchedule')}
          </button>
        )}
      </div>

      {adding && (
        <ScheduleEntryForm
          allSchedules={schedules}
          onSave={(data) => {
            onAdd(data);
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {schedules.length === 0 && !adding && (
        <p className="text-sm text-ash">{t('admin.noSchedules')}</p>
      )}

      {schedules.length > 0 && (
        <div className="flex gap-1 overflow-x-auto border-b border-ink/10 pb-px -mx-4 px-4 sm:mx-0 sm:px-0">
          {viewModes.map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => setViewMode(mode.id)}
              className={`shrink-0 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                viewMode === mode.id
                  ? 'border-ink text-ink'
                  : 'border-transparent text-ash hover:text-ink'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>
      )}

      {viewMode === 'prayer' ? (
        <div className="space-y-4">
          {prayerGroups.map((group) => (
            <PrayerGroup
              key={group.prayerType}
              prayerType={group.prayerType}
              entries={group.entries}
              schedules={schedules}
              editingId={editingId}
              isHebrew={isHebrew}
              t={t}
              onEdit={(id) => {
                setEditingId(id);
                setAdding(false);
              }}
              onCancelEdit={() => setEditingId(null)}
              onUpdate={onUpdate}
              onDelete={handleDelete}
              onReorder={onReorder}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {dayGroups.map((group) => (
            <div key={group.dayType} className="space-y-2">
              <p className="text-xs font-medium text-ash uppercase tracking-wide">
                {t(`dayTypes.${group.dayType}`)}
              </p>
              <ul className="space-y-2">
                {group.entries.map((entry) => {
                  if (editingId === entry.id) {
                    // Entry may appear in multiple day groups; show the form once.
                    const firstDay = DAY_TYPES.find(
                      (d) =>
                        Array.isArray(entry.dayTypes) && entry.dayTypes.includes(d)
                    );
                    if (group.dayType !== firstDay) return null;
                    return (
                      <li key={`${group.dayType}-${entry.id}`}>
                        <ScheduleEntryForm
                          entry={entry}
                          allSchedules={schedules}
                          onSave={(data) => {
                            onUpdate(entry.id, data);
                            setEditingId(null);
                          }}
                          onCancel={() => setEditingId(null)}
                        />
                      </li>
                    );
                  }

                  return (
                    <li
                      key={`${group.dayType}-${entry.id}`}
                      className="flex items-start justify-between gap-3 rounded-[10px] border border-ink/10 px-3 py-2"
                    >
                      <EntryBody
                        entry={entry}
                        isHebrew={isHebrew}
                        t={t}
                        onEdit={() => {
                          setEditingId(entry.id);
                          setAdding(false);
                        }}
                        onDelete={() => handleDelete(entry.id)}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
